<script lang="ts" setup>
import { computed, onMounted, reactive, Ref, ref } from 'vue';
import { useTranslate } from '@/locales/i18n';
import * as Sentry from '@sentry/vue';
import { Tooltip } from 'ant-design-vue';
import 'ant-design-vue/lib/tooltip/style/index.css';
import { useApi } from '@/mw-components/Api';
import { useToast } from 'vue-toastification';
import SurveyClosedSvg from '@/assets/svg/unpublished.svg?component';
import Loading from '@/components/Loading.vue';
import { isValidEmail, isValidLocation, isValidNumber } from '@/plugin/functions';
import router from '../../router';
import PublicLayout from '../Shared/PublicLayout.vue';

// Modern support files
import { SurveyQuestionType } from './enums';
import { AnswerModel, ApiPayload, ErrorByElementId, QuestionModel, SectionModel } from './types';
import helpers from './helpers';

// Modern components
import BasicNotificationVue from '@/components/notifications/BasicNotification.vue';
import AcelQuestion from '@/views/Survey/Question/Acel.vue';
import Comment from '@/views/Survey/Question/Comment.vue';
import Dropdown from '@/views/Survey/Question/Dropdown.vue';
import Location from '@/views/Survey/Question/Location.vue';
import MultipleChoiceCheckbox from '@/views/Survey/Question/MultipleChoiceCheckbox.vue';
import MultipleChoiceRadio from '@/views/Survey/Question/MultipleChoiceRadio.vue';
import MultiQuestion from '@/views/Survey/Question/MultiQuestion.vue';
import NumberRating from '@/views/Survey/Question/NumberRating.vue';
import SingleNumber from '@/views/Survey/Question/SingleNumber.vue';
import SingleText from '@/views/Survey/Question/SingleText.vue';
import Distance from '@/views/Survey/Question/Distance.vue';
import DaysPerWeek from '@/views/Survey/Question/DaysPerWeek.vue';

const api = useApi(import.meta.env.mode);
const toast = useToast();
const translate = useTranslate();

const props = defineProps<{
  preview: string; // This is a string because it's a query parameter
}>();

const publicSurveyId = ref('');
const contactId = ref('');
const isPreview = ref(true); // defaulting true before it's updated from the props in
const alreadyAnswered = ref(false);

const mandatoryQuestionIds: Ref<string[]> = ref([]);
const mandatorySections: Ref<SectionModel[]> = ref([]);
const errors: Ref<ErrorByElementId[]> = ref([]);

const data = reactive({
  surveyData: {} as any,
  answers: [] as AnswerModel[],
  contentReady: false,
  showError: false,
  isSurveyComplete: false,
  hasMandatory: false,
  imageAlignment: 'justify-center',
  backgroundAlignment: 'center center',
});

const submitPending = ref(false);

onMounted(() => {
  // Preview surveys have ?preview=true in the URL
  isPreview.value = Boolean(props.preview);

  // Get the survey id and contact id from the URL
  const params = router.currentRoute.value.params;
  publicSurveyId.value = String(params.publicId);
  contactId.value = String(params.contactId);

  alreadyAnswered.value = helpers.checkIfAlreadyAnswered(publicSurveyId, contactId);
  getSurveyData();
});

const getSurveyData = (): void => {
  api
    .SurveyDataPublic(publicSurveyId.value, contactId.value)
    .then((resp) => {
      data.surveyData = { ...resp };
      data.contentReady = true;
      document.title = data.surveyData.surveyTitle || translate('survey.defaultTitle');

      data.hasMandatory = data.surveyData.sections.some((section: SectionModel) => {
        return section.questions.some((question) => question.mandatory);
      });

      // Filter list of all mandatory questions
      mandatorySections.value = data.surveyData.sections.filter((section: SectionModel) => {
        return section.questions.filter((question) => question.mandatory).length;
      });

      mandatoryQuestionIds.value = getMandatoryQuestionIds();
    })
    .catch(() => {
      data.contentReady = true;
      data.showError = true;
    });
};

const modifyQuestionLabel = (question: any, sectionIndex: number, questionIndex: number) => ({
  ...question,
  questionText: data.surveyData.numbered
    ? `${sectionIndex + 1}${questionIndex > 0 ? '.' + questionIndex : '.'} ${question.questionText}`
    : question.questionText,
});

const onAnswerChanged = (answerModel: AnswerModel) => {
  // Does this answer already exist in the array? Get its index.
  const existingAnswerIndex = data.answers.findIndex(
    (answer: AnswerModel) => answer.id === answerModel.id,
  );
  // If it exists
  if (existingAnswerIndex >= 0) {
    // Remove it from the array if it is empty
    if (answerModel.answer === '') {
      data.answers.splice(existingAnswerIndex, 1);
    } else {
      // Otherwise, update the answer
      data.answers[existingAnswerIndex] = answerModel;
    }
    // We're done
    //return;

    // But if it doesn't exist already
  } else {
    // Check the answer is answered and add it to the array of answers
    if (answerModel.answer !== '') data.answers.push(answerModel);
  }
  // @todo Check on this - what if the answer is required?
  helpers.clearSingleError(answerModel.id);
};

const onSurveySubmit = () => {
  // Refresh the mandatoryIds because the user might have submitted some 'Other' options
  mandatoryQuestionIds.value = getMandatoryQuestionIds(data.answers);
  if (!validateAnswers()) {
    helpers.scrollToFirstError();
    return;
  }

  // Don't allow submissions in preview mode
  if (isPreview.value) {
    data.isSurveyComplete = true;
    return;
  }

  const payload: ApiPayload = {
    publicSurveyId: publicSurveyId.value,
    answers: data.answers,
  };

  if (contactId.value) payload.contactId = contactId.value;

  submitPending.value = true;
  api
    .SurveyAnswer(payload)
    .then(() => {
      data.isSurveyComplete = true;
      helpers.updateAnsweredSurveys(publicSurveyId, contactId);
    })
    .catch((err) => {
      Sentry.captureException(err, {
        extra: payload,
      });
      toast.error(translate('survey.errors.completeAllQuestions'));
      submitPending.value = false;
    });
};

/**
 * Refactored validation that keeps it all in one place
 * @param answerModel
 * @param questionType
 * @param questionId
 */
function validateAnswer(
  answerModel: AnswerModel,
  questionType: SurveyQuestionType,
  questionId: string,
): boolean {
  if (answerModel.answer === '') return false;
  const answer = answerModel.answer;
  let valid: boolean;
  switch (questionType) {
    // Number rating fields = number string between 0 and 10
    case SurveyQuestionType.NumberRating:
      valid = typeof answer === 'string' && Number(answer) >= 0 && Number(answer) <= 10;
      if (!valid) {
        errors.value.push(
          helpers.addErrorByElementId(translate('survey.errors.enterValidNumber'), questionId),
        );
      }
      break;

    // ACEL fields = mode enum (number)
    case SurveyQuestionType.Acel:
      valid = typeof answer === 'number';
      if (!valid) {
        errors.value.push(
          helpers.addErrorByElementId(translate('survey.errors.selectModeOfTransport'), questionId),
        );
      }
      break;

    // Email fields  = valid email
    case SurveyQuestionType.EmailAddress:
      valid = isValidEmail(String(answer));
      if (!valid) {
        errors.value.push(
          helpers.addErrorByElementId(translate('survey.errors.enterValidEmail'), questionId),
        );
      }
      break;

    // Location fields = string comprising a location, e.g. "Warsaw, Poland" and stringified object containing two numbers, e.g. "{52.2297,21.0122}"
    // Use the helper function in the randonly named 'plugin' folder
    case SurveyQuestionType.Location:
      valid = typeof answer === 'string' && isValidLocation(String(answer));
      if (!valid) {
        errors.value.push(
          helpers.addErrorByElementId(translate('survey.errors.selectValidLocation'), questionId),
        );
      }
      break;

    // Number fields = valid number or string which converts to a number
    case SurveyQuestionType.Number:
      valid =
        typeof answer === 'number' || (typeof answer === 'string' && isValidNumber(String(answer)));
      if (!valid) {
        errors.value.push(
          helpers.addErrorByElementId(translate('survey.errors.enterNumber'), questionId),
        );
      }
      break;

    // Days per week = number
    case SurveyQuestionType.DaysPerWeek:
      valid = typeof answer === 'string';
      if (!valid) {
        errors.value.push(
          helpers.addErrorByElementId(translate('survey.errors.enterNumber'), questionId),
        );
      }
      break;

    // Distance = string containing a pipe character followed by 'miles' or 'km' and the number should be more than 0
    case SurveyQuestionType.Distance:
      valid =
        typeof answer === 'string' &&
        (answer.split('|')[1] === 'miles' || answer.split('|')[1] === 'km') &&
        Number(answer.split('|')[0]) >= 0;
      if (!valid) {
        errors.value.push(
          helpers.addErrorByElementId(translate('survey.errors.enterValidDistance'), questionId),
        );
      }
      break;

    // Comment = string no more than 5000 characters
    case SurveyQuestionType.Comment:
      valid = typeof answer === 'string' && String(answer).length <= 2000;
      if (!valid) {
        errors.value.push(
          helpers.addErrorByElementId(
            translate('survey.errors.noLongerThan5000Characters'),
            questionId,
          ),
        );
      }
      break;

    // Single text = string no more than 2000 characters
    case SurveyQuestionType.SingleText:
      valid = typeof answer === 'string' && String(answer).length <= 2000;
      if (!valid) {
        errors.value.push(
          helpers.addErrorByElementId(
            translate('survey.errors.noLongerThan2000Characters'),
            questionId,
          ),
        );
      }
      break;

    // Default used for multiple choice questions (radio, dropdown, checkbox) = just check for an answer
    default:
      valid = String(answer).length > 0;
      if (!valid) {
        errors.value.push(
          helpers.addErrorByElementId(translate('survey.errors.selectAnOption'), questionId),
        );
      }
  }
  return valid;
}

const validateAnswers = () => {
  errors.value = [];
  helpers.resetErrors();

  // Trying out alternative validation
  // Loop through every section
  const sections: SectionModel[] = data.surveyData.sections;
  sections.forEach((section) => {
    // Loop through every question
    const questions: QuestionModel[] = section.questions;
    questions.forEach((question) => {
      const answer = data.answers.find((ans) => ans.id === question.id);
      // Handle unanswered required questions (not multi questions, though @todo)
      if (
        (question.mandatory &&
          !answer &&
          question.questionType !== SurveyQuestionType.MultiQuestion) ||
        (answer && answer.answer === '')
      ) {
        errors.value.push(
          helpers.addErrorByElementId(translate('survey.errors.completeThisQuestion'), question.id),
        );
        return false;
      }
      if (answer) return validateAnswer(answer, question.questionType, question.id);

      // Loop through every child question
      const cQuestions = question.childQuestions;
      if (cQuestions) {
        cQuestions.forEach((cQuestion) => {
          const cAnswer = data.answers.find((ans) => ans.id === cQuestion.id);
          // Handle unanswered required questions
          if ((cQuestion.mandatory && !cAnswer) || (cAnswer && cAnswer.answer === '')) {
            errors.value.push(
              helpers.addErrorByElementId(
                translate('survey.errors.completeThisQuestion'),
                cQuestion.id,
              ),
            );
            return false;
          }
          if (cAnswer) return validateAnswer(cAnswer, cQuestion.questionType, cQuestion.id);
        });
      }
    });
  });

  if (errors.value.length > 0) {
    toast.error(translate('survey.errors.checkYourAnswers'));
    return false;
  }
  if (!data.answers.length) {
    toast.error(translate('survey.errors.youHaveNotAnsweredAnyQuestions'));
    return false;
  }
  return true;
};

// recursively walk through all child questions until we reach the bottom, checking each id
const walkChildQuestions = (
  childQuestions: Array<QuestionModel>,
  questionId: string,
): string | null => {
  let questionDetails = null;

  childQuestions.forEach((childQuestion) => {
    if (childQuestion.childQuestions && childQuestion.childQuestions.length) {
      let foundQuestion = walkChildQuestions(childQuestion.childQuestions, questionId);
      if (foundQuestion) questionDetails = foundQuestion;
    }

    if (childQuestion.id === questionId) {
      questionDetails = childQuestion;
    }
  });

  return questionDetails;
};

const isSurveyPublished = () => {
  if (!data.surveyData || isPreview.value || data.surveyData.status === undefined) return true;
  return data.surveyData.status === 1;
};

const imageSrc = computed(
  () =>
    data.surveyData.emailWelcomeImageUrl ||
    'https://liftshare.blob.core.windows.net/static-assets/mobilityways/dashboard/mw_full_logo_220.png',
);

const imageStyle = computed(() => {
  return helpers.convertImageSizeToCssStyle(
    data.surveyData.emailWelcomeImageUrl,
    data.surveyData.imageWidth,
    data.surveyData.imageHeight,
  );
});

const imageAlign = computed(() => {
  return helpers.getImageAlignmentClass(data.surveyData.imageHorizontalPosition);
});

// Returns an array of mandatoryIds, including checking for questions with an 'Other' option
function getMandatoryQuestionIds(answers?: AnswerModel[]): string[] {
  return mandatorySections.value.flatMap((section: SectionModel) => {
    return section.questions.flatMap((question) => {
      if (question.questionType === SurveyQuestionType.MultiQuestion && question.childQuestions) {
        return question.childQuestions.map((childQuestion) => childQuestion.id);
      }

      // The following successfully validates the child question for a dropdown Other,
      // but prevents validation for the dropdown itself... no good.
      // if (question.questionType === SurveyQuestionType.Dropdown) {
      //   return question.childQuestions.map(
      //     (childQuestion) => childQuestion.id
      //   );
      // }
      // So, we also need to check the submitted answers and if the answer == 'Other' then we return the mapped childQuestion.
      // So, we need the data, and this function has to be called onSubmit as well as onMount, hence it's here.
      if (
        (question.questionType === SurveyQuestionType.Dropdown ||
          question.questionType === SurveyQuestionType.MultipleChoiceCheckbox ||
          question.questionType === SurveyQuestionType.MultipleChoiceRadio) &&
        answers
      ) {
        // Does the question have an 'Other' option AND is it selected?
        const questionHasOtherOptionSelected = answers.find(
          (answer) =>
            answer.id == question.id &&
            answer.answer == translate('survey.errors.otherPleaseSpecify'),
        );
        // If yes, identify the child question as required
        if (questionHasOtherOptionSelected && question.childQuestions) {
          return question.childQuestions.map((childQuestion) => childQuestion.id);
        }
      }

      return question.id;
    });
  });
}
</script>

<template>
  <PublicLayout classes="p-0">
    <div v-if="isPreview" class="fixed right-4 top-4 z-30">
      <BasicNotificationVue
        :message="translate('survey.preview.thisSurveyIsForPreviewPurposes')"
        :title="translate('survey.preview.previewSurveyOnly')"
        status="warning"
      />
    </div>
    <div
      v-if="data.contentReady"
      class="lg:flex flex-col pt-12 xl:pt-28 items-start justify-center flex-1 relative rich-text-underline-links rich-text-lists text-center"
    >
      <template v-if="isSurveyPublished()">
        <div
          :style="{
            'background-color': data.surveyData.accentColour ?? '#4299e1',
          }"
          class="absolute top-0 left-0 w-full h-40 md:h-56"
        ></div>

        <div class="mx-auto w-full max-w-2xl md:mt-12">
          <div
            class="bg-white px-6 py-8 m-4 md:m-0 md:px-12 md:py-24 flex flex-col justify-between relative z-10 shadow"
          >
            <template v-if="data.isSurveyComplete">
              <div
                v-if="data.surveyData.showThankYouImage"
                class="w-full flex {{ data.imageAlignment }}"
              >
                <div
                  v-if="data.surveyData.surveyThankyouImageUrl"
                  :style="{
                    'background-image': `url(${data.surveyData.surveyThankyouImageUrl})`,
                    'background-position': data.backgroundAlignment,
                  }"
                  class="w-full h-20 mb-12 bg-contain bg-no-repeat bg-center"
                ></div>
                <img
                  v-else
                  alt="Mobilityways"
                  class="mb-12"
                  src="https://liftshare.blob.core.windows.net/static-assets/mobilityways/dashboard/mw_full_logo_220.png"
                />
              </div>
            </template>

            <template v-else>
              <div v-if="data.surveyData.showWelcomeImage" :class="imageAlign" class="w-full flex">
                <img :src="imageSrc" :style="imageStyle" alt="" class="mb-12" />
              </div>
            </template>

            <template v-if="data.showError">
              <p class="text-xl md:text-3xl mb-6 font-bold">{{ translate('survey.oops') }}</p>
              <p class="mb-12">
                {{ translate('survey.unableToLoadSurvey') }}
              </p>
            </template>

            <template v-else-if="alreadyAnswered || data.surveyData.hasAnsweredSurvey">
              <p class="text-3xl mb-6 font-bold">{{ translate('survey.thanks') }}</p>
              <p class="mb-12">
                {{ translate('survey.alreadyAnswered') }}
              </p>
            </template>

            <template v-else-if="!data.isSurveyComplete">
              <p v-if="data.surveyData.showSurveyTitle" class="text-left text-xl md:text-3xl mb-6 font-bold">
                {{ data.surveyData.surveyTitle ?? translate('survey.defaultTitle') }}
              </p>
              <div
                v-if="data.surveyData.showSurveyIntroduction"
                :class="data.hasMandatory ? 'mb-4' : 'mb-10'"
                class="text-left richtext whitespace-pre-wrap"
                v-html="
                  data.surveyData.surveyIntroduction ??
                  translate('survey.answerTheFollowingQuestions')
                "
              ></div>

              <p v-if="data.hasMandatory" class="text-center text-xs mb-12">
                {{ translate('survey.errors.questionsMarkedWith') }}
                <span class="text-red-500">*</span> {{ translate('survey.errors.requireAnAnswer') }}
              </p>

              <!--              <div class="flex flex-row">-->
              <!--                <button-->
              <!--                  class="bg-pink-500 text-white py-1.5 px-3 mr-1.5"-->
              <!--                  @click="setI18nLanguage('en-GB')"-->
              <!--                >-->
              <!--                  English-->
              <!--                </button>-->
              <!--                <button-->
              <!--                  class="bg-pink-500 text-white py-1.5 px-3 mr-1.5"-->
              <!--                  @click="setI18nLanguage('cy-GB')"-->
              <!--                >-->
              <!--                  Cymraeg-->
              <!--                </button>-->
              <!--                <button-->
              <!--                  class="bg-pink-500 text-white py-1.5 px-3"-->
              <!--                  @click="setI18nLanguage('ja-JP')"-->
              <!--                >-->
              <!--                  日本語-->
              <!--                </button>-->
              <!--              </div>-->

              <template
                v-for="(section, sectionIndex) in data.surveyData.sections"
                :key="sectionIndex"
              >
                <section>
                  <template v-for="(question, index) in section.questions">
                    <div
                      :data-survey-question="question.id"
                      :data-survey-question-type="question.questionType"
                    >
                      <NumberRating
                        v-if="question.questionType === SurveyQuestionType.NumberRating"
                        :key="index"
                        :accentColour="data.surveyData.accentColour"
                        :question="modifyQuestionLabel(question, sectionIndex, index)"
                        @answerChanged="onAnswerChanged"
                      />

                      <SingleText
                        v-if="question.questionType === SurveyQuestionType.SingleText"
                        :key="index"
                        :question="modifyQuestionLabel(question, sectionIndex, index)"
                        @answerChanged="onAnswerChanged"
                      />

                      <SingleNumber
                        v-if="question.questionType === SurveyQuestionType.Number"
                        :key="index"
                        :question="modifyQuestionLabel(question, sectionIndex, index)"
                        @answerChanged="onAnswerChanged"
                      />

                      <Comment
                        v-if="question.questionType === SurveyQuestionType.Comment"
                        :key="index"
                        :question="modifyQuestionLabel(question, sectionIndex, index)"
                        @answerChanged="onAnswerChanged"
                      />

                      <MultipleChoiceCheckbox
                        v-if="question.questionType === SurveyQuestionType.MultipleChoiceCheckbox"
                        :key="index"
                        :question="modifyQuestionLabel(question, sectionIndex, index)"
                        @answerChanged="onAnswerChanged"
                        @childAnswerChanged="onAnswerChanged"
                      />

                      <MultipleChoiceRadio
                        v-if="question.questionType === SurveyQuestionType.MultipleChoiceRadio"
                        :key="index"
                        :question="modifyQuestionLabel(question, sectionIndex, index)"
                        @answerChanged="onAnswerChanged"
                        @childAnswerChanged="onAnswerChanged"
                      />

                      <Dropdown
                        v-if="question.questionType === SurveyQuestionType.Dropdown"
                        :key="index"
                        :question="modifyQuestionLabel(question, sectionIndex, index)"
                        @answerChanged="onAnswerChanged"
                        @childAnswerChanged="onAnswerChanged"
                      />
                      <AcelQuestion
                        v-if="question.questionType === SurveyQuestionType.Acel"
                        :key="index"
                        :question="modifyQuestionLabel(question, sectionIndex, index)"
                        @answerChanged="onAnswerChanged"
                        @childAnswerChanged="onAnswerChanged"
                      />

                      <Location
                        v-if="question.questionType === SurveyQuestionType.Location"
                        :key="index"
                        :question="modifyQuestionLabel(question, sectionIndex, index)"
                        @answerChanged="onAnswerChanged"
                      />

                      <MultiQuestion
                        v-if="question.questionType === SurveyQuestionType.MultiQuestion"
                        :key="index"
                        :question="modifyQuestionLabel(question, sectionIndex, index)"
                        @answerChanged="onAnswerChanged"
                      />

                      <Distance
                        v-if="question.questionType === SurveyQuestionType.Distance"
                        :key="index"
                        :errors="errors"
                        :is-child-question="false"
                        :multi-question="false"
                        :question="modifyQuestionLabel(question, sectionIndex, index)"
                        @answerChanged="onAnswerChanged"
                      />
                      <DaysPerWeek
                        v-if="question.questionType === SurveyQuestionType.DaysPerWeek"
                        :key="index"
                        :errors="errors"
                        :is-child-question="false"
                        :multi-question="false"
                        :question="modifyQuestionLabel(question, sectionIndex, index)"
                        @answerChanged="onAnswerChanged"
                      />
                    </div>
                  </template>
                </section>
              </template>

              <Tooltip placement="bottom">
                <template v-if="isPreview" #title>
                  {{ translate('survey.preview.previewSurveyOnly') }}
                </template>
                <button
                  :class="{ 'opacity-50': isPreview || submitPending }"
                  :disabled="isPreview || submitPending"
                  :style="{
                    'background-color': data.surveyData.accentColour ?? '#4299e1',
                  }"
                  class="bg-pri text-white w-full md:w-auto px-12 text-lg py-3 self-center hover:opacity-75 ease-in duration-300 inline-block rounded text-center"
                  type="button"
                  @click="onSurveySubmit"
                >
                  {{ submitPending ? translate('survey.submitting') : translate('survey.submit') }}
                </button>
              </Tooltip>
            </template>

            <template v-else>
              <p class="text-3xl mb-6 font-bold">
                {{ data.surveyData.surveyThankyouTitle ?? translate('survey.thankYou') }}
              </p>
              <p
                class="whitespace-pre-wrap"
                v-html="
                  data.surveyData.surveyThankyouMessage ??
                  translate('survey.yourFeedbackIsAppreciated')
                "
              ></p>
            </template>
          </div>

          <p v-if="!data.showError" class="text-center text-xs mt-8 mb-8">
            {{ translate('survey.smallPrint.yourDataIsBeingProcessedBy') }}
            {{ data.surveyData.emailFromName || 'Mobilityways' }}
            {{ translate('survey.smallPrint.toEnsureThey') }}
          </p>
          <p v-if="!data.showError" class="text-center text-xs mt-8 mb-8 mx-auto max-w-xl">
            {{ translate('survey.smallPrint.forMoreInformation') }}
            <a class="underline" href="https://mobilityways.com/privacy-policy/" target="_blank">{{
              translate('survey.smallPrint.privacyNotice')
            }}</a
            >.
          </p>
          <a href="https://www.mobilityways.com/">
            <img
              alt="Mobilityways"
              class="mt-8 mb-16 w-24 mx-auto"
              src="https://liftshare.blob.core.windows.net/static-assets/mobilityways/dashboard/mw_full_logo_220.png"
            />
          </a>
        </div>
      </template>

      <template v-else>
        <div
          class="absolute top-0 left-0 w-screen h-screen bg-gray-100 flex flex-col items-center justify-center p-6"
        >
          <SurveyClosedSvg class="max-w-xs md:max-w-md" />
          <p class="font-bold text-lg md:text-2xl mt-8 text-center">
            {{ translate('survey.surveyClosed') }}
          </p>
        </div>
      </template>
    </div>

    <div v-else>
      <Loading />
    </div>
  </PublicLayout>
</template>
